import { getAddress, zeroAddress } from "viem";

import type { MarketsInfoData } from "types/markets";
import type { TradeAction as SubsquidTradeAction } from "types/subsquid";
import { Token, TokenData, TokensData } from "types/tokens";
import type { PositionTradeAction } from "types/tradeHistory";
import type { SwapTradeAction } from "types/tradeHistory";

import { getByKey } from "./objects";
import { isIncreaseOrderType, isSwapOrderType } from "./orders";
import { getSwapPathOutputAddresses } from "./swap/swapStats";
import { parseContractPrice } from "./tokens";
import { TradeActionType } from "../types/tradeHistory";

const safeAddress = (address: string | null | undefined): string => {
  if (!address || address === "null" || !address.startsWith("0x")) {
    return zeroAddress;
  }
  try {
    return getAddress(address);
  } catch (_error) {
    return zeroAddress;
  }
};

export function createRawTradeActionTransformer(
  marketsInfoData: MarketsInfoData,
  wrappedToken: Token,
  tokensData: TokensData
): (
  value: SubsquidTradeAction,
  index: number,
  array: SubsquidTradeAction[]
) => SwapTradeAction | PositionTradeAction | undefined {
  return (rawAction, index, array) => {
    try {
      const orderType = Number(rawAction.orderType);
      const executionType = Number.isFinite(Number((rawAction as any).executionType))
        ? Number((rawAction as any).executionType)
        : undefined;

      if (isSwapOrderType(orderType)) {
        const initialCollateralTokenAddress = safeAddress(rawAction.initialCollateralTokenAddress);
        let resolvedInitialCollateralDeltaAmount = bigNumberify(rawAction.initialCollateralDeltaAmount) || 0n;
        let resolvedSwapPath = (rawAction.swapPath || []).map((address) => safeAddress(address));

        // Lookback mechanism: if swapPath is empty or amount is 0, try to find context from previous events of the same order
        if (resolvedSwapPath.length === 0 || resolvedInitialCollateralDeltaAmount === 0n) {
          const relatedAction = (rawAction as any).orderKey ? array.find(
            (a, i) => i < index && (a as any).orderKey === (rawAction as any).orderKey && (a.eventName === TradeActionType.OrderCreated || a.eventName === TradeActionType.OrderUpdated)
          ) : undefined;

          if (relatedAction) {
            if (resolvedSwapPath.length === 0 && relatedAction.swapPath) {
              resolvedSwapPath = relatedAction.swapPath.map((address: string) => safeAddress(address));
            }
            if (resolvedInitialCollateralDeltaAmount === 0n && relatedAction.initialCollateralDeltaAmount) {
              resolvedInitialCollateralDeltaAmount = bigNumberify(relatedAction.initialCollateralDeltaAmount) || 0n;
            }
          }
        }

        const swapPathOutputAddresses = getSwapPathOutputAddresses({
          marketsInfoData,
          swapPath: resolvedSwapPath,
          initialCollateralAddress: initialCollateralTokenAddress,
          wrappedNativeTokenAddress: wrappedToken.address,
          shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
          isIncrease: false,
        });

        const initialCollateralToken = getByKey(tokensData, initialCollateralTokenAddress);
        const targetCollateralToken = getByKey(tokensData, swapPathOutputAddresses.outTokenAddress);

        // Fallback: if token data is unavailable (e.g. not yet loaded), create a stub
        const DECIMALS_18 = 18;
        const stubToken = (address: string, name?: string) => ({
          address,
          decimals: DECIMALS_18,
          symbol: name ?? (address === zeroAddress ? "ETH" : address.slice(0, 6) + "..."),
          name: name ?? (address === zeroAddress ? "ETH" : address.slice(0, 6) + "..."),
          prices: { minPrice: 0n, maxPrice: 0n },
          totalSupply: 0n,
          balance: 0n,
        });

        const resolvedInitialCollateralToken = initialCollateralToken ?? stubToken(initialCollateralTokenAddress);
        const resolvedTargetCollateralToken = targetCollateralToken ?? stubToken(swapPathOutputAddresses.outTokenAddress!);

        const tradeAction: SwapTradeAction = {
          id: rawAction.id,
          eventName: rawAction.eventName as TradeActionType,
          account: rawAction.account,
          swapPath: resolvedSwapPath,
          orderType,
          executionType,
          orderKey: rawAction.orderKey,
          initialCollateralTokenAddress: rawAction.initialCollateralTokenAddress!,
          initialCollateralDeltaAmount: resolvedInitialCollateralDeltaAmount,
          minOutputAmount: bigNumberify(rawAction.minOutputAmount) || 0n,
          executionAmountOut: rawAction.executionAmountOut ? bigNumberify(rawAction.executionAmountOut) : undefined,
          shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
          targetCollateralToken: resolvedTargetCollateralToken as TokenData,
          initialCollateralToken: resolvedInitialCollateralToken as TokenData,
          timestamp: rawAction.timestamp,
          transaction: (rawAction as any).transaction,
          reason: rawAction.reason ?? undefined,
          reasonBytes: rawAction.reasonBytes ?? undefined,
          twapParams:
            rawAction.twapGroupId && rawAction.numberOfParts
              ? {
                twapGroupId: rawAction.twapGroupId,
                numberOfParts: rawAction.numberOfParts,
              }
              : undefined,
        };

        return tradeAction;
      } else {
        const marketAddress = safeAddress(rawAction.marketAddress);
        const marketInfo = getByKey(marketsInfoData, marketAddress);
        const indexToken = marketInfo?.indexToken;
        const initialCollateralTokenAddress = safeAddress(rawAction.initialCollateralTokenAddress);
        const swapPath = (rawAction.swapPath || []).map((address) => safeAddress(address));
        const swapPathOutputAddresses = getSwapPathOutputAddresses({
          marketsInfoData,
          swapPath,
          initialCollateralAddress: initialCollateralTokenAddress,
          wrappedNativeTokenAddress: wrappedToken.address,
          shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
          isIncrease: isIncreaseOrderType(rawAction.orderType),
        });
        const initialCollateralToken = getByKey(tokensData, initialCollateralTokenAddress);
        const targetCollateralToken = getByKey(tokensData, swapPathOutputAddresses.outTokenAddress);

        // Fallback: if market or token data is unavailable (e.g. not yet loaded), create a stub
        // so the trade still appears in the history table rather than being silently dropped.
        const DECIMALS_18 = 18;
        const stubToken = (address: string, name?: string) => ({
          address,
          decimals: DECIMALS_18,
          symbol: name ?? address.slice(0, 6) + "...",
          name: name ?? address.slice(0, 6) + "...",
          prices: { minPrice: 0n, maxPrice: 0n },
          totalSupply: 0n,
          balance: 0n,
        });
        const stubMarketInfo = (marketAddr: string, collateralAddr: string) => ({
          // Minimal fields needed by PositionTradeAction
          marketTokenAddress: marketAddr,
          indexTokenAddress: collateralAddr,
          longTokenAddress: collateralAddr,
          shortTokenAddress: collateralAddr,
          isSameCollaterals: false,
          isSpotOnly: false,
          isDisabled: false,
          indexToken: stubToken(collateralAddr),
          longToken: stubToken(collateralAddr),
          shortToken: stubToken(collateralAddr),
          // Add all remaining market fields as safe defaults
          longPoolAmount: 0n,
          shortPoolAmount: 0n,
          maxLongPoolAmount: 0n,
          maxShortPoolAmount: 0n,
          maxLongPoolUsdForDeposit: 0n,
          maxShortPoolUsdForDeposit: 0n,
          longPoolAmountAdjustment: 0n,
          shortPoolAmountAdjustment: 0n,
          poolValueMax: 0n, poolValueMin: 0n,
          netPnlMax: 0n, netPnlMin: 0n,
          totalBorrowingFees: 0n,
          positionImpactPoolAmount: 0n,
          swapImpactPoolAmountLong: 0n,
          swapImpactPoolAmountShort: 0n,
          borrowingFactorPerSecondForLongs: 0n,
          borrowingFactorPerSecondForShorts: 0n,
          fundingFactorPerSecond: 0n,
          longsPayShorts: false,
          virtualPoolAmountForLongToken: 0n,
          virtualPoolAmountForShortToken: 0n,
          virtualInventoryForPositions: 0n,
          virtualMarketId: undefined,
          virtualLongTokenId: undefined,
          virtualShortTokenId: undefined,
          minCollateralFactor: 0n,
          minCollateralFactorForOpenInterestLong: 0n,
          minCollateralFactorForOpenInterestShort: 0n,
          maxOpenInterestForLongs: 0n,
          maxOpenInterestForShorts: 0n,
          reserveFactorLong: 0n,
          reserveFactorShort: 0n,
          openInterestReserveFactorLong: 0n,
          openInterestReserveFactorShort: 0n,
          maxPnlFactorForTradersLong: 0n,
          maxPnlFactorForTradersShort: 0n,
          claimableFundingAmountLong: 0n,
          claimableFundingAmountShort: 0n,
          positionFeeFactorForPositiveImpact: 0n,
          positionFeeFactorForNegativeImpact: 0n,
          positionImpactFactorPositive: 0n,
          positionImpactFactorNegative: 0n,
          maxPositionImpactFactorPositive: 0n,
          maxPositionImpactFactorNegative: 0n,
          maxPositionImpactFactorForLiquidations: 0n,
          swapFeeFactorForPositiveImpact: 0n,
          swapFeeFactorForNegativeImpact: 0n,
          atomicSwapFeeFactor: 0n,
          swapImpactFactorPositive: 0n,
          swapImpactFactorNegative: 0n,
          minPositionImpactPoolAmount: 0n,
          positionImpactPoolDistributionRate: 0n,
          openInterestLong: 0n,
          openInterestShort: 0n,
          openInterestInTokensLong: 0n,
          openInterestInTokensShort: 0n,
          pnlLongMax: 0n, pnlLongMin: 0n,
          pnlShortMax: 0n, pnlShortMin: 0n,
          borrowingExponentFactorLong: 0n,
          borrowingExponentFactorShort: 0n,
          fundingExponentFactor: 0n,
          fundingIncreaseFactorPerSecond: 0n,
          fundingDecreaseFactorPerSecond: 0n,
          thresholdForStableFunding: 0n,
          thresholdForDecreaseFunding: 0n,
          minFundingFactorPerSecond: 0n,
          maxFundingFactorPerSecond: 0n,
        } as any);

        const resolvedMarketInfo = marketInfo ?? stubMarketInfo(marketAddress, initialCollateralTokenAddress);
        const resolvedIndexToken = indexToken ?? stubToken(initialCollateralTokenAddress);
        const resolvedInitialCollateralToken = initialCollateralToken ?? stubToken(initialCollateralTokenAddress);
        const resolvedTargetCollateralToken = targetCollateralToken ?? stubToken(initialCollateralTokenAddress);

        const tradeAction: PositionTradeAction = {
          id: rawAction.id,
          eventName: rawAction.eventName as TradeActionType,
          account: rawAction.account,
          marketAddress,
          marketInfo: resolvedMarketInfo,
          indexToken: resolvedIndexToken,
          swapPath,
          initialCollateralTokenAddress,
          initialCollateralToken: resolvedInitialCollateralToken,
          targetCollateralToken: resolvedTargetCollateralToken,
          initialCollateralDeltaAmount: bigNumberify(rawAction.initialCollateralDeltaAmount)!,
          sizeDeltaUsd: bigNumberify(rawAction.sizeDeltaUsd)!,
          triggerPrice: rawAction.triggerPrice
            ? parseContractPrice(bigNumberify(rawAction.triggerPrice)!, resolvedIndexToken.decimals)
            : undefined,
          acceptablePrice: rawAction.acceptablePrice
            ? parseContractPrice(bigNumberify(rawAction.acceptablePrice)!, resolvedIndexToken.decimals)
            : 0n,
          executionPrice: rawAction.executionPrice
            ? parseContractPrice(bigNumberify(rawAction.executionPrice)!, resolvedIndexToken.decimals)
            : undefined,
          minOutputAmount: bigNumberify(rawAction.minOutputAmount)!,

          collateralTokenPriceMax: rawAction.collateralTokenPriceMax
            ? parseContractPrice(bigNumberify(rawAction.collateralTokenPriceMax)!, resolvedInitialCollateralToken.decimals)
            : undefined,

          collateralTokenPriceMin: rawAction.collateralTokenPriceMin
            ? parseContractPrice(bigNumberify(rawAction.collateralTokenPriceMin)!, resolvedInitialCollateralToken.decimals)
            : undefined,

          indexTokenPriceMin: rawAction.indexTokenPriceMin
            ? parseContractPrice(bigNumberify(rawAction.indexTokenPriceMin) || 0n, resolvedIndexToken.decimals)
            : undefined,
          indexTokenPriceMax: rawAction.indexTokenPriceMax
            ? parseContractPrice(bigNumberify(rawAction.indexTokenPriceMax) || 0n, resolvedIndexToken.decimals)
            : undefined,

          orderType,
          executionType,
          orderKey: rawAction.orderKey,
          isLong: rawAction.isLong!,
          pnlUsd: rawAction.pnlUsd ? BigInt(rawAction.pnlUsd) : undefined,
          pnlSource: (rawAction as any).pnlSource ?? undefined,
          basePnlUsd: rawAction.basePnlUsd ? BigInt(rawAction.basePnlUsd) : undefined,

          priceImpactDiffUsd: rawAction.priceImpactDiffUsd ? bigNumberify(rawAction.priceImpactDiffUsd) : undefined,
          priceImpactUsd: rawAction.priceImpactUsd ? bigNumberify(rawAction.priceImpactUsd) : undefined,
          positionFeeAmount: rawAction.positionFeeAmount ? bigNumberify(rawAction.positionFeeAmount) : undefined,
          borrowingFeeAmount: rawAction.borrowingFeeAmount ? bigNumberify(rawAction.borrowingFeeAmount) : undefined,
          fundingFeeAmount: rawAction.fundingFeeAmount ? bigNumberify(rawAction.fundingFeeAmount) : undefined,
          liquidationFeeAmount: rawAction.liquidationFeeAmount ? bigNumberify(rawAction.liquidationFeeAmount) : undefined,

          reason: rawAction.reason ?? undefined,
          reasonBytes: rawAction.reasonBytes ?? undefined,

          transaction: (rawAction as any).transaction,
          timestamp: rawAction.timestamp,
          shouldUnwrapNativeToken: rawAction.shouldUnwrapNativeToken!,
          twapParams:
            rawAction.twapGroupId && rawAction.numberOfParts
              ? {
                twapGroupId: rawAction.twapGroupId,
                numberOfParts: rawAction.numberOfParts,
              }
              : undefined,
        };

        return tradeAction;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("transformer error", e, rawAction);
      return undefined;
    }
  };
}

export function bigNumberify(n?: bigint | string | null | undefined) {
  try {
    if (n === undefined) throw new Error("n is undefined");
    if (n === null) throw new Error("n is null");

    return BigInt(n);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error("bigNumberify error", e);
    return undefined;
  }
}
